import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useLanguage } from '../../../../context/LanguageContext';
import Navbar from '../../../components/navbar/ui/Navbar';
import Footer from '../../../components/footer/ui/Footer';
import WhatsAppButton from '../../../components/whatsapp-button/ui/WhatsAppButton';
import ScrollToTop from '../../../components/scroll-to-top/ui/ScrollToTop';
import SEO from '../../../components/seo/SEO';
import HelpCard from '../../../components/help-card/ui/HelpCard';
import PageHeader from '../components/ui/PageHeader';
import ProductSection from '../components/ui/ProductSection';
import ProductDetailDialog from '../components/ui/ProductDetailDialog';
import { seoData, getStoreCategorySEO } from '../../../../data/seoData';

const StorePage: React.FC = () => {
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();
    const { categorySlug } = useParams<{ categorySlug?: string }>();
    
    // Product Detail Dialog State
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Handle opening product detail dialog
    const handleOpenProductDialog = (product: any) => {
        setSelectedProduct(product);
        setIsDialogOpen(true);
    };

    // Handle closing product detail dialog
    const handleCloseProductDialog = () => {
        setIsDialogOpen(false);
        setTimeout(() => setSelectedProduct(null), 300);
    };

    // Handle add to wishlist
    const handleAddToWishlist = (productId: string) => {
        // Call the global addToWishlist function from store-firebase.js
        if (typeof (window as any).addToWishlist === 'function') {
            (window as any).addToWishlist(productId);
        }
    };

    useEffect(() => {
        // Expose language context to window for Firebase script
        (window as any).LanguageContext = {
            getCurrentLanguage: () => language
        };

        // Dispatch languageChanged event after updating window context
        // This ensures categories and other dynamic content update with the new language
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('languageChanged', {
                detail: { language }
            }));
        }, 100);

        // Expose navigation function to window for product links
        (window as any).navigateToProduct = (productId: string) => {
            navigate(`/produto/${productId}`);
        };
        
        // Expose navigation function to window for category links
        (window as any).navigateToCategory = (categorySlug: string | null) => {
            if (categorySlug) {
                navigate(`/loja/${categorySlug}`);
            } else {
                navigate('/loja');
            }
        };
        
        // Expose navigation function to budget page
        (window as any).navigateToBudget = () => {
            navigate('/orcamento');
        };
        
        // Expose function to open product dialog from store-firebase.js
        (window as any).openProductDialog = handleOpenProductDialog;
        

        // Load required scripts in order
        const loadScripts = async () => {
            // Load CategoryEnum (no longer loading language-context.js - React Context handles this)
            const contextScripts = [
                '/data/enum/CategoryEnum.js'
            ];

            for (const src of contextScripts) {
                await new Promise<void>((resolve) => {
                    const script = document.createElement('script');
                    script.src = src;
                    script.onload = () => resolve();
                    script.onerror = () => {
                        resolve(); // Continue even if it fails
                    };
                    document.head.appendChild(script);
                });
            }

            // Import store logic from component script
            // Note: Dynamically importing as the script uses Firebase which needs to be loaded first
            // @ts-expect-error - store-firebase.js is a plain JS file without type declarations
            import('../components/script/store-firebase.js').then((module) => {
                
                // Update store language with current language from React
                if (module.updateStoreLanguage && typeof module.updateStoreLanguage === 'function') {
                    module.updateStoreLanguage(language);
                }
            }).catch(() => {
            });
        };

        loadScripts();

        return () => {
            // Cleanup
            delete (window as any).navigateToProduct;
            delete (window as any).navigateToCategory;
            delete (window as any).navigateToBudget;
            delete (window as any).openProductDialog;
        };
    }, [language, navigate]);

    // Handle route changes (category slug from pathname) to refresh filter
    useEffect(() => {
        // Wait a bit for the Firebase script to be ready
        const timer = setTimeout(() => {
            // Check if the store script has initialized
            if (typeof (window as any).refreshStoreFilter === 'function') {
                // Call the refresh function with the category slug
                (window as any).refreshStoreFilter(categorySlug || null);
            } else {
                // If function not available yet, try again after a delay
                setTimeout(() => {
                    if (typeof (window as any).refreshStoreFilter === 'function') {
                        (window as any).refreshStoreFilter(categorySlug || null);
                    }
                }, 500);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
        };
    }, [categorySlug, location.pathname]);

    // Get SEO data based on category
    const seo = categorySlug ? getStoreCategorySEO(categorySlug) : seoData.store;

    return (
        <>
            <SEO 
                title={seo.title}
                description={seo.description}
                keywords={seo.keywords}
                canonical={seo.canonical}
            />
        <div className="store-page">
            <Navbar />
            <PageHeader />
            <ProductSection />
            <section className="container">
                <HelpCard />
            </section>
            <Footer />
            <WhatsAppButton />
            <ScrollToTop />
            
            {/* Product Detail Dialog */}
            {isDialogOpen && selectedProduct && (
                <ProductDetailDialog
                    product={selectedProduct}
                    onClose={handleCloseProductDialog}
                    onAddToWishlist={handleAddToWishlist}
                />
            )}
        </div>
        </>
    );
};

export default StorePage;

